<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import CommentsFilters from '../components/CommentsFilters.vue';
import CommentsList from '../components/CommentsList.vue';
import CommentDetail from '../components/CommentDetail.vue';
import CommentReplyBox from '../components/CommentReplyBox.vue';
import PostsIndex from './PostsIndex.vue';
import { useSocialCommentsStore } from 'dashboard/stores/socialComments';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const POLL_INTERVAL_MS = 30000;

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// comment_management and post_publishing clear Meta App Review
// independently (see Api::V1::Accounts::SocialPostsController), so an
// account can hold post_publishing without comment_management. The Comments
// tab needs the narrower flag; the Posts tab stays reachable either way.
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
// A flag reads false both when it is off and when the account simply has not
// been fetched yet. Only the redirect needs to tell those apart, so it waits
// for the record to exist before treating a false flag as authoritative.
const getAccount = useMapGetter('accounts/getAccount');
const isAccountLoaded = computed(() =>
  Boolean(getAccount.value(accountId.value)?.id)
);
const hasCommentManagement = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.COMMENT_MANAGEMENT
  )
);

const isCommentsTab = computed(
  () => hasCommentManagement.value && route.name === 'comments_dashboard_index'
);

// A stale bookmark, browser back button, or hand-typed URL can still land an
// account with comment_management off on the Comments route -- the router
// guard only enforces meta.permissions, not the feature flag (see
// routes/index.js). Bounce to Posts rather than render an empty, 403ing
// Comments view.
const redirectAwayFromDisabledCommentsTab = () => {
  if (
    !hasCommentManagement.value &&
    route.name === 'comments_dashboard_index'
  ) {
    router.replace({
      name: 'comments_dashboard_posts',
      params: { accountId: route.params.accountId },
    });
  }
};

const commentsStore = useSocialCommentsStore();
const { records, uiFlags } = storeToRefs(commentsStore);

const getInboxes = useMapGetter('inboxes/getInboxes');
const facebookInboxes = computed(() =>
  getInboxes.value.filter(inbox => inbox.channel_type === INBOX_TYPES.FB)
);

const filters = ref({ inboxId: undefined, state: 'needs_reply' });
const selectedId = ref(null);

const selectedComment = computed(
  () => records.value.find(record => record.id === selectedId.value) || null
);

// Only treat the very first, empty-list fetch as a loading state: the
// 30 second poll below also flips fetchingList, and showing the skeleton
// on every silent background refresh would blank out the list the admin
// is currently reading every half minute.
const isLoading = computed(
  () => uiFlags.value.fetchingList && records.value.length === 0
);

// The page's own reply reaches our database only when Meta's `feed` webhook lands, a moment
// after the Graph call returns -- so the open thread has to be refreshed on the same cadence
// as the queue. CommentDetail only reloads it when the selected post changes, which meant an
// agent's reply never appeared under "Replies" for as long as they stayed on the comment.
const refreshThread = () => {
  const socialPostId = selectedComment.value?.socialPost?.id;
  if (!socialPostId) return Promise.resolve();

  return commentsStore.fetchThread(socialPostId).catch(() => {});
};

const fetchComments = async () => {
  await commentsStore.get({
    inboxId: filters.value.inboxId,
    state: filters.value.state,
  });
  await refreshThread();
};

const handleFiltersChange = value => {
  filters.value = value;
  fetchComments();
};

const handleSelect = id => {
  selectedId.value = id;
};

const replyBoxRef = ref(null);

// Every reply is a Graph write that can fail -- an expired page token is the ordinary case --
// and the store rethrows Meta's own message. Surface that, and leave the typed text in the box
// until the request has actually succeeded: clearing on click lost the agent's words to a
// silent rejection while the comment stayed unhandled.
const sendReply = async send => {
  try {
    await send();
    replyBoxRef.value?.onSuccess();
  } catch (error) {
    useAlert(error.message || t('COMMENTS.REPLY_BOX.ERROR'));
  }
};

const handleReply = message =>
  sendReply(() =>
    commentsStore.reply({ id: selectedComment.value.id, message })
  );

const handlePrivateReply = message =>
  sendReply(() =>
    commentsStore.privateReply({ id: selectedComment.value.id, message })
  );

let pollTimer = null;

// The account record -- and therefore its feature flags -- can land after this
// component mounts, so reading the flag once in a mount hook sees `false` on a
// cold load and never retries. That silently left the queue empty and, worse,
// bounced an enabled account away from its own tab. Watch the flag instead, and
// only act once, the first time it resolves true.
let hasStarted = false;
watch(
  hasCommentManagement,
  enabled => {
    if (!enabled || hasStarted) return;

    hasStarted = true;
    fetchComments();
    pollTimer = setInterval(fetchComments, POLL_INTERVAL_MS);
  },
  { immediate: true }
);

// Redirecting is only safe once the account has actually loaded: a flag that is
// merely not-yet-fetched must not be read as "disabled", or an account with
// comment_management on gets bounced off the Comments tab on every cold load.
watch(
  [isAccountLoaded, hasCommentManagement],
  ([loaded]) => {
    if (loaded) redirectAwayFromDisabledCommentsTab();
  },
  { immediate: true }
);

onUnmounted(() => {
  clearInterval(pollTimer);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      v-if="isCommentsTab"
      class="flex flex-col gap-4 px-6 py-4 border-b border-n-weak"
    >
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('COMMENTS.HEADER') }}
      </h1>
    </header>
    <div class="flex flex-1 overflow-hidden">
      <template v-if="isCommentsTab">
        <div class="flex flex-col h-full border-r shrink-0 w-96 border-n-weak">
          <CommentsFilters
            :inboxes="facebookInboxes"
            :model-value="filters"
            @update:model-value="handleFiltersChange"
          />
          <CommentsList
            :comments="records"
            :selected-id="selectedId"
            :is-loading="isLoading"
            @select="handleSelect"
          />
        </div>
        <div class="flex flex-col flex-1 overflow-hidden">
          <CommentDetail :comment="selectedComment" class="flex-1" />
          <CommentReplyBox
            v-if="selectedComment"
            ref="replyBoxRef"
            :comment="selectedComment"
            @reply="handleReply"
            @private-reply="handlePrivateReply"
          />
        </div>
      </template>
      <PostsIndex v-else />
    </div>
  </section>
</template>
